import React, { useState, useEffect } from 'react';
import { Link, useLoaderData } from 'react-router-dom';
import { motion } from 'framer-motion';
import Seo from 'components/Seo';
import Section from 'components/Section';
import AnimationPage from 'components/AnimationPage';
import arrow from 'assets/images/icon-arrow-back.svg';
import animations from 'utils/animations';
import "assets/styles/CaseStudy.css";

export default function CaseStudy() {
  const { work, projects } = useLoaderData(); 
  const [showScrollTop, setShowScrollTop] = useState(false);

  const { 
    seo, 
    hero, 
    tags, 
    client, 
    title, 
    overview, 
    new: news, 
    challenge, 
    approach, 
    feature, 
    recognitions 
  } = work;

  const { opacity, opacityAndMove } = animations.components;

  // encontrar el índice actual
  const currentIndex = projects.findIndex(p => p.slug === work.slug);

  // índices circulares
  const prevIndex = (currentIndex - 1 + projects.length) % projects.length;
  const nextIndex = (currentIndex + 1) % projects.length;

  const prev = projects[prevIndex];
  const next = projects[nextIndex];

  // Mostrar botón cuando se supera el final del hero
  useEffect(() => {
    const heroEl = document.querySelector('.case-hero');

    const handleScroll = () => {
      if (!heroEl) return;
      const heroBottom = heroEl.offsetTop + heroEl.offsetHeight;
      setShowScrollTop(window.scrollY > heroBottom);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // init por si ya carga scrolleado
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <AnimationPage>
      <Seo
        title={`${seo?.title + ' - ' || ''}Work - Kollectiff`}
        description={seo?.description}
        image={seo?.image || `${hero.image}?h=630&w=1200`} 
      />
      
      <main className="case-study-template mt-[-6.5rem] relative">
      
        <div className="case-hero" style={{ backgroundImage: `url(${hero.image})` }}>
          <div className="container"> 
            <div className="case-title">
              {tags.map((tag) => (
                <motion.p
                  key={tag}
                  className="tag-md"
                  {...opacityAndMove}
                  transition={{ delay: 0.2 }}
                >
                  {tag}
                </motion.p>
              ))}
              <motion.p
                {...opacity}
                transition={{ delay: .3 }}
                className="k-title-xs-regular xl:k-title-sm-regular">
                  {client}
              </motion.p>
              <motion.h1 
                {...opacityAndMove}
                transition={{ delay: .4 }}
                className="k-title-sm xl:k-title-md">
                  {title}
              </motion.h1>
            </div>

            <motion.div 
              {...opacityAndMove}
              transition={{ delay: .5 }}
            >
              <Link to="/work" className="case-arrow-link link-animated">
                <img src={arrow} alt="" /><span className='link-sm'>Back to works</span>
              </Link>
            </motion.div>
          </div>
        </div>

        <div className="case-content">
          <Section
            name="overview"
            id="overview"
            title={overview.title}
            description={overview.description}
            media={overview.media}
          >
            <div className='case-details'>
              <motion.div 
                {...opacity}
                transition={{ delay: .3 }}>
                <h3 className='k-text-md xl:k-text-lg font-bold'>Project</h3>
                <p className='k-text-md xl:k-text-lg'>{overview.project}</p>
              </motion.div>

              <motion.div 
                {...opacity}
                transition={{ delay: .3 }}>
                <h3 className='k-text-md xl:k-text-lg font-bold'>Services</h3>
                <p className='k-text-md xl:k-text-lg'>{overview.services}</p>
              </motion.div>
            </div>

            {recognitions && (
              <motion.div 
                className="case-recognitions"
                {...opacity}
                transition={{ delay: .4 }}>
                <span className="tag-sm">Awards & Recognitions</span>
                <div className="case-recognitions-list">
                  {recognitions.map(recognition =>
                    <a 
                      key={recognition.title}
                      href={recognition.link} 
                      target="_blank" 
                      rel="noreferrer" 
                      className="award-card"
                    >
                      <img src={recognition.logo} alt={recognition.title} />
                      {recognition.title}
                    </a>
                  )}
                </div>

              </motion.div>
            )}
          </Section>
        
          {news && (
            <section className="case-new">
              <div className="container">
                <div 
                  className="case-block" 
                  style={{ backgroundColor: news.backgroundColor }}
                >
                  <h2 className="k-title-xs md:k-title-sm">{news.title}</h2>
                  <span className="k-text-lg">- <a 
                      href={news.link} 
                      rel="noreferrer" 
                      target="_blank" 
                      className="k-text-lg underline"
                    >
                     {news.source}
                    </a>
                  </span>
                </div>
              </div>
            </section>
          )}

          <Section
            name="challenges"
            title={challenge.title}
            description={challenge.description}
            media={challenge.media} 
            id="challenges"
          />

          {approach.title && (
            <Section
              name="approach"
              title={approach.title}
              description={approach.description}
              media={approach.media}
              id="approach"
            />
          )}

          {feature.title && (
            <Section
              name="features"
              title={feature.title}
              description={feature.description}
              media={feature.media} 
              id="features"
            />
          )}
        </div>

        <div className="container footer-menu">
          {prev && (
            <Link to={'/work/' + prev.slug} className="case-arrow-link link-animated">
              <img src={arrow} alt="" /> Previous project
            </Link>
          )}
          {next && (
            <Link to={'/work/' + next.slug} className="case-arrow-link link-animated">
              Next project <img src={arrow} alt="" className="rotate-180" />
            </Link>
          )}
        </div>

        {/* Botón dentro del main */}
        {showScrollTop && (
          <button
            type="button"
            onClick={scrollToTop}
            className="scroll-to-top-btn"
            aria-label="Scroll to top"
            title="Back to top"
          >
            <span className='sr-only'>Back to top</span>
          </button>
        )}
      </main>
    </AnimationPage>
  );
}